import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@shared/v2/IconButton";
import { Mailboxflagupsolid, FeatherRegularV6 } from "@shared/v2/Icomoon";
import { Popover, PopoverTrigger, PopoverContent, PopoverItem } from "@shared/v2/Popover";
import Tooltip from "@shared/v2/Tooltip";
import AddressPickerModal from "@shared/v2/AddressPickerModal";
import { isMailableAddress } from "@shared/v2/utils";
import {
  useHandwrittenCard,
  HandwrittenCardModal,
  getAddressSortPriority,
  getInvalidReason,
} from "@shared/v2/HandwrittenCard";
import { useStandardizedAddresses } from "@shared/v2/hooks";
import { mapToContactPayload } from "../../PersonDetail/components/listing_alerts/helper";
import SelectMailerTypeModal from "../../PersonDetail/components/modals/SelectMailerTypeModal";

const SendMailMenu = ({ person, isSendMailDisabled, showHandwrittenCard }) => {
  const [showMailerModal, setShowMailerModal] = useState(false);

  const {
    isLoading,
    iframeUrl,
    iframeRef,
    error,
    isAddressPickerOpen,
    isModalOpen,
    fetchIframeUrl,
    closeModal,
    openAddressPicker,
    closeAddressPicker,
  } = useHandwrittenCard();

  // Only fetch standardization data when address picker is needed (lazy load)
  const { addresses, standardizedAddresses, isLoading: isLoadingStandardization, enableFetch } =
    useStandardizedAddresses(person, person?.slug, { includeVerifiedStatus: false });

  const sortedAddresses = useMemo(
    () =>
      [...standardizedAddresses].sort((a, b) => getAddressSortPriority(a) - getAddressSortPriority(b)),
    [standardizedAddresses],
  );
  const mailableAddresses = useMemo(() => addresses.filter(isMailableAddress), [addresses]);
  const hasAddresses = addresses.length > 0;
  const canSendMailer = mailableAddresses.length > 0;
  const hasFirstName = Boolean(person?.first_name?.trim());

  const getHandwrittenCardTooltip = () => {
    if (hasFirstName && canSendMailer) return null;

    if (hasFirstName) {
      return hasAddresses
        ? "Update this lead to have a valid mailing address to send a handwritten card."
        : "Add an address to this lead to send a handwritten card.";
    }
    if (canSendMailer) return "Add a first name to this lead to send a handwritten card.";

    return hasAddresses
      ? "Add a first name to this lead and update it to have a valid mailing address to send a handwritten card."
      : "Add a first name and an address to this lead to send a handwritten card.";
  };

  const handleSendMail = () => {
    setShowMailerModal(true);
  };

  const handleSendHandwrittenCard = () => {
    if (!hasFirstName || !canSendMailer) return;
    if (addresses.length === 1 && mailableAddresses.length === 1) {
      fetchIframeUrl(person?.uuid, mailableAddresses[0].id);
    } else if (addresses.length >= 1 && mailableAddresses.length >= 1) {
      enableFetch();
      openAddressPicker();
    }
  };

  const handleAddressSelect = (address) => {
    closeAddressPicker();
    fetchIframeUrl(person?.uuid, address.id);
  };

  // If handwritten card is disabled, just show the mail button directly (no dropdown)
  if (!showHandwrittenCard) {
    return (
      <>
        <IconButton
          disabled={isSendMailDisabled}
          onClick={handleSendMail}
          aria-label="Send Mail"
        >
          <Mailboxflagupsolid />
        </IconButton>
        {!isSendMailDisabled && (
          <SelectMailerTypeModal
            setOpen={() => setShowMailerModal(!showMailerModal)}
            isModalOpen={showMailerModal}
            personData={mapToContactPayload([person])}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Popover placement="bottom-start">
        <PopoverTrigger>
          <IconButton
            disabled={isSendMailDisabled}
            aria-label="Send Mail Options"
          >
            <Mailboxflagupsolid />
          </IconButton>
        </PopoverTrigger>
        <PopoverContent className="tw-z-3000 tw-min-w-[180px]">
          <PopoverItem
            icon={<Mailboxflagupsolid size="m" />}
            onClick={handleSendMail}
          >
            Send Mail
          </PopoverItem>
          <Tooltip
            placement="left"
            multiline
            innerClassName="tw-max-w-[200px]"
            content={getHandwrittenCardTooltip()}
            trigger={
              <PopoverItem
                icon={<FeatherRegularV6 size="m" />}
                onClick={handleSendHandwrittenCard}
                disabled={!hasFirstName || !canSendMailer}
              >
                Send Handwritten Card
              </PopoverItem>
            }
          />
        </PopoverContent>
      </Popover>

      {!isSendMailDisabled && (
        <SelectMailerTypeModal
          setOpen={() => setShowMailerModal(!showMailerModal)}
          isModalOpen={showMailerModal}
          personData={mapToContactPayload([person])}
        />
      )}

      <AddressPickerModal
        show={isAddressPickerOpen}
        onHide={closeAddressPicker}
        addresses={sortedAddresses}
        onSelect={handleAddressSelect}
        getInvalidReason={getInvalidReason}
        isLoading={isLoadingStandardization}
      />

      <HandwrittenCardModal
        show={isModalOpen}
        onHide={closeModal}
        isLoading={isLoading}
        iframeUrl={iframeUrl}
        iframeRef={iframeRef}
        error={error}
      />
    </>
  );
};

SendMailMenu.propTypes = {
  person: PropTypes.shape({
    uuid: PropTypes.string,
    slug: PropTypes.string,
    first_name: PropTypes.string,
    included: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isSendMailDisabled: PropTypes.bool,
  showHandwrittenCard: PropTypes.bool,
};

SendMailMenu.defaultProps = {
  person: null,
  isSendMailDisabled: true,
  showHandwrittenCard: false,
};

export default SendMailMenu;
